import Apis from '@/Apis/_'
import EventBus from '@/plugins/EventBus'
import Vue from 'vue'
import { MedicoProps, UseMedicosState } from './types'
import { RequestOptionsProps } from '../types'
import useLoading from '../useLoading'

const useMedicos = (() => {
  const state = Vue.observable<UseMedicosState>({
    fetchedMedicosByInstrumentador: false,
    fetchedMedicosByCurrentUser: false,
    hospitalUtilizaRegraDeMedicoExecutante: false,
    medicoPrescritorEhConfiguradoParaExecutarCirurgias: false,
    hospitalNaoPossuiMedicosExecutantesConfigurados: false,
    medicosByInstrumentador: [],
    medicosByCurrentUser: [],
  })

  const { closeLoading, showLoading } = useLoading()

  function setMedicosByInstrumentador(data: MedicoProps[]) {
    state.medicosByInstrumentador = data
  }

  function setMedicosByCurrentUser(data: MedicoProps[]) {
    state.medicosByCurrentUser = data
  }

  function setHospitalUtilizaRegraDeMedicoExecutante(data: boolean) {
    state.hospitalUtilizaRegraDeMedicoExecutante = data
  }

  function setMedicoPrescritorEhConfiguradoParaExecutarCirurgias(data: boolean) {
    state.medicoPrescritorEhConfiguradoParaExecutarCirurgias = data
  }

  function setHospitalNaoPossuiMedicosExecutantesConfigurados(data: boolean) {
    state.hospitalNaoPossuiMedicosExecutantesConfigurados = data
  }

  async function loadMedicosByInstrumentador({
    showLoading: shouldShowLoading = true,
    forceRefresh = false,
    showToastError = true,
  }: RequestOptionsProps = {}) {
    if (state.fetchedMedicosByInstrumentador && !forceRefresh) {
      return { success: true, data: state.medicosByInstrumentador }
    }
    if (shouldShowLoading) showLoading()
    const response = await Apis.Agendamento.SolicitacaoCirurgiaApi.getAllMedicoByInstrumentadorId()
    if (response.success) {
      setMedicosByInstrumentador(response.data)
      state.fetchedMedicosByInstrumentador = true
    } else if (showToastError) {
      EventBus.$emit('setSnackbar', {
        show: true,
        text: 'Ocorreu um erro na requisição.',
        icon: 'warning',
        color: '#C62828',
      })
    }
    closeLoading()
    return response
  }

  async function loadMedicosByCurrentUser({
    showLoading: shouldShowLoading = true,
    forceRefresh = false,
    showToastError = true,
  }: RequestOptionsProps = {}) {
    if (state.fetchedMedicosByCurrentUser && !forceRefresh) {
      return { success: true, data: state.medicosByCurrentUser }
    }
    if (shouldShowLoading) showLoading()
    const response = await Apis.Agendamento.SolicitacaoCirurgiaApi.getMedicoshospitalByUserCurrent()
    if (response.success) {
      setMedicosByCurrentUser(response.data)
      state.fetchedMedicosByCurrentUser = true
    } else if (showToastError) {
      EventBus.$emit('setSnackbar', {
        show: true,
        text: 'Ocorreu um erro na requisição.',
        icon: 'warning',
        color: '#C62828',
      })
    }
    closeLoading()
    return response
  }

  async function loadMedicosHospitaisPorUsuarioAtual({
    showLoading: shouldShowLoading = true,
    forceRefresh = false,
    showToastError = true,
    params,
  }: RequestOptionsProps = {}) {
    if (state.fetchedMedicosByCurrentUser && !forceRefresh) {
      return { success: true, data: state.medicosByCurrentUser }
    }
    if (shouldShowLoading) showLoading()
    const response = await Apis.Agendamento.SolicitacaoCirurgiaApi.getMedicosHospitalPorUsuarioAtual(
      params,
    )
    if (response.success) {
      setMedicosByCurrentUser(response.data)
      state.fetchedMedicosByCurrentUser = true
    } else if (showToastError) {
      EventBus.$emit('setSnackbar', {
        show: true,
        text: 'Ocorreu um erro na requisição.',
        icon: 'warning',
        color: '#C62828',
      })
    }
    closeLoading()
    return response
  }

  async function loadMedicosExecutantesHospitais({
    showLoading: shouldShowLoading = true,
    forceRefresh = false,
    params,
  }: RequestOptionsProps = {}) {
    if (state.fetchedMedicosByCurrentUser && !forceRefresh) return
    if (shouldShowLoading) showLoading()
    const response = await Apis.Agendamento.SolicitacaoCirurgiaApi.getMedicosExecutantes(params)
    if (response.success) {
      const {
        hospitalUtilizaRegraDeMedicoExecutante,
        medicoPrescritorEhConfiguradoParaExecutarCirurgias,
        hospitalNaoPossuiMedicosExecutantesConfigurados,
        medicosExecutantes,
      } = response.data
      setMedicosByCurrentUser(medicosExecutantes)
      setHospitalUtilizaRegraDeMedicoExecutante(hospitalUtilizaRegraDeMedicoExecutante)
      setMedicoPrescritorEhConfiguradoParaExecutarCirurgias(medicoPrescritorEhConfiguradoParaExecutarCirurgias)
      setHospitalNaoPossuiMedicosExecutantesConfigurados(hospitalNaoPossuiMedicosExecutantesConfigurados)
      state.fetchedMedicosByCurrentUser = true
    } else {
      EventBus.$emit('setSnackbar', {
        show: true,
        text: 'Ocorreu um erro na requisição.',
        icon: 'warning',
        color: '#C62828',
      })
    }
    closeLoading()
  }

  return {
    get medicosByInstrumentador() {
      return state.medicosByInstrumentador
    },
    get medicosByCurrentUser() {
      return state.medicosByCurrentUser
    },
    loadMedicosByInstrumentador,
    loadMedicosByCurrentUser,
    loadMedicosHospitaisPorUsuarioAtual,
    loadMedicosExecutantesHospitais,
    setMedicosByInstrumentador,
    setMedicosByCurrentUser,
    get hospitalNaoPossuiMedicosExecutantesConfigurados() {
      return state.hospitalNaoPossuiMedicosExecutantesConfigurados
    },
    setHospitalNaoPossuiMedicosExecutantesConfigurados,
    get hospitalUtilizaRegraDeMedicoExecutante() {
      return state.hospitalUtilizaRegraDeMedicoExecutante
    },
    setHospitalUtilizaRegraDeMedicoExecutante,
    get medicoPrescritorEhConfiguradoParaExecutarCirurgias() {
      return state.medicoPrescritorEhConfiguradoParaExecutarCirurgias
    },
    setMedicoPrescritorEhConfiguradoParaExecutarCirurgias,
  }
})()

export default useMedicos
